const ACCESS_TOKEN_KEY = "accessToken";
const CHAT_TOKEN_KEY = "chat_token";

// In-memory token storage (replaces localStorage for security)
// refresh token is stored in HttpOnly cookie, not accessible to JS
let memoryTokens: {
  accessToken: string | null;
} = {
  accessToken: null,
};

// Migration flag to ensure we only migrate once
let migrationCompleted = false;

// Migration helper: migrate from localStorage on first load (one-time)
// Note: refresh tokens are now in HttpOnly cookies, not localStorage
export const migrateTokensFromLocalStorage = (): { accessToken: string | null } => {
  if (migrationCompleted || typeof window === 'undefined') {
    return { accessToken: memoryTokens.accessToken };
  }

  try {
    const storage = window.localStorage;
    const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
    const oldRefreshToken = storage.getItem('refreshToken'); // old key name

    if (accessToken) {
      console.log('[authTokens] Migrating access token from localStorage to memory');
      memoryTokens.accessToken = accessToken;

      // Clear localStorage after migration
      storage.removeItem(ACCESS_TOKEN_KEY);
      storage.removeItem('token'); // legacy
      
      // clear old refresh token from localStorage (now in HttpOnly cookie)
      if (oldRefreshToken) {
        storage.removeItem('refreshToken');
        console.log('[authTokens] Cleared old refresh token from localStorage (now in HttpOnly cookie)');
      }
      
      console.log('[authTokens] Migration complete, localStorage cleared');
    }

    migrationCompleted = true;
    return { accessToken };
  } catch (error) {
    console.error('[authTokens] Error during migration:', error);
    migrationCompleted = true;
    return { accessToken: null };
  }
};

export const getAccessToken = (): string | null => {
  return memoryTokens.accessToken;
};

export const getAuthToken = (): string | null => {
  return getAccessToken();
};

// refresh token is stored in HttpOnly cookie, not accessible to JS
// use the refresh endpoint with credentials: 'include' instead
export const getRefreshToken = (): string | null => {
  return null; // refresh token is in HttpOnly cookie, not accessible to JS
};

export const getChatToken = () => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.localStorage.getItem(CHAT_TOKEN_KEY);
  } catch (error) {
    console.error('[authTokens] Error getting chat token:', error);
    return null;
  }
};

export const setAuthTokens = (tokens: { accessToken?: string; refreshToken?: string }) => {
  if (tokens.accessToken) {
    memoryTokens.accessToken = tokens.accessToken;
    console.debug('[authTokens] Stored access token in memory, length:', tokens.accessToken.length);
  }

  // refresh token is stored in HttpOnly cookie by backend, not in JS
  if (tokens.refreshToken) {
    console.debug('[authTokens] Ignoring refreshToken parameter - refresh token is stored in HttpOnly cookie');
  }
};

export const clearAuthTokens = () => {
  memoryTokens.accessToken = null;
  // refresh token is in HttpOnly cookie, cleared by backend on logout
  console.debug('[authTokens] Cleared access token from memory');
};

// diagnostic function to check for token storage state
export const diagnoseStorageContext = () => {
  if (typeof window === 'undefined') {
    return { error: 'Not in browser context' };
  }

  try {
    const origin = window.location.origin;
    const href = window.location.href;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;

    const accessToken = memoryTokens.accessToken;

    const info = {
      origin,
      href,
      protocol,
      hostname,
      port,
      storageType: 'memory',
      hasAccessToken: !!accessToken,
      refreshTokenStorage: 'HttpOnly cookie (not accessible to JS)',
      accessTokenLength: accessToken?.length || 0,
      isInFrame: window.self !== window.top,
      parentOrigin: window.self !== window.top ? document.referrer : null,
      migrationCompleted,
    };

    console.log('[authTokens] Token storage diagnostic:', info);
    return info;
  } catch (error) {
    console.error('[authTokens] Error diagnosing storage context:', error);
    return { error: String(error) };
  }
};
